<app-loading-overlay [show]="isLoading"/>

<div class="card">
    <div class="card-header">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <h4 class="mb-0">Histórico de Execuções</h4>
                <small class="text-muted">Histórico de sincronizações com a base de dados da PGFN</small>
            </div>

            <div class="d-flex align-items-center gap-2">
                <!-- Status Indicator -->
                @if (isSyncRunning()) {
                    <div class="alert alert-dark mb-0 py-2 px-3 d-flex align-items-center">
                        <div class="spinner-border spinner-border-sm me-2" role="status">
                            <span class="visually-hidden">Sincronizando...</span>
                        </div>
                        <small><strong>Sincronização em andamento...</strong></small>
                    </div>
                }

                <!-- Sync Button -->
                <button class="btn btn-primary"
                        (click)="startSync()"
                        [disabled]="isSyncRunning() || isSyncing()">
                    @if (isSyncing()) {
                        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    } @else {
                        <i class="ti ti-refresh me-2"></i>
                    }
                    {{ isSyncRunning() ? 'Sincronizando...' : 'Nova Sincronização' }}
                </button>
            </div>
        </div>

        <!-- Filters -->
        <div class="row g-3">
            <div class="col-md-3">
                <app-select
                    [(ngModel)]="filters.year"
                    label="Ano"
                    [items]="years"
                    [clearable]="true"
                    placeholder="Todos os anos"
                    (ngModelChange)="search()">
                </app-select>
            </div>

            <div class="col-md-3">
                <app-select
                    [(ngModel)]="filters.state"
                    label="Estado"
                    [items]="states"
                    bindLabel="name"
                    bindValue="value"
                    [clearable]="true"
                    placeholder="Todos os estados"
                    (ngModelChange)="search()">
                </app-select>
            </div>

            <div class="col-md-3">
                <app-select
                    [(ngModel)]="filters.status"
                    label="Status"
                    [items]="statusOptions"
                    bindLabel="name"
                    bindValue="value"
                    [clearable]="true"
                    placeholder="Todos os status"
                    (ngModelChange)="search()">
                </app-select>
            </div>
        </div>
    </div>

    <div class="card-body p-0">
        <app-table [tableData]="items">
            <!-- Header da Tabela -->
            <ng-template app-table-header>
                <th scope="col" class="ps-4" style="width: 50px;"></th>
                <th scope="col"
                    appTableHeaderColumn
                    labelKey="Data/Hora Início"
                    sortField="startTime"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col"
                    appTableHeaderColumn
                    labelKey="Ano"
                    sortField="year"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col"
                    appTableHeaderColumn
                    labelKey="Estado"
                    sortField="state"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col"
                    appTableHeaderColumn
                    labelKey="Status"
                    sortField="status"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col"
                    appTableHeaderColumn
                    labelKey="Duração"
                    sortField="duration"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col" class="text-end"
                    appTableHeaderColumn
                    labelKey="Registros"
                    sortField="recordsProcessed"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col" class="text-end"
                    appTableHeaderColumn
                    labelKey="Empresas"
                    sortField="companiesSaved"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
                <th scope="col" class="text-end pe-4"
                    appTableHeaderColumn
                    labelKey="Débitos"
                    sortField="debtsSaved"
                    [currentSort]="currentSort"
                    (sortChange)="onSort($event)">
                </th>
            </ng-template>

            <!-- Body da Tabela -->
            <ng-template app-table-body let-item>
                <td class="ps-4">
                    @if (item.errorMessage) {
                        <button class="btn btn-sm btn-link p-0 text-danger"
                                (click)="toggleExpandRow(item.id)"
                                [attr.aria-expanded]="expandedRowId() === item.id">
                            <i class="ti"
                               [class.ti-chevron-down]="expandedRowId() !== item.id"
                               [class.ti-chevron-up]="expandedRowId() === item.id"></i>
                        </button>
                    }
                </td>
                <td>{{ item.startTime | date:'dd/MM/yyyy HH:mm:ss' }}</td>
                <td>{{ item.year }}</td>
                <td>{{ getStateLabel(item.state) }}</td>
                <td>
                    <span [class]="getStatusBadgeClass(item.status)">
                        {{ getStatusLabel(item.status) }}
                    </span>
                </td>
                <td>{{ item.duration | duration }}</td>
                <td class="text-end">{{ item.recordsProcessed ?? 0 | number }}</td>
                <td class="text-end">{{ item.companiesSaved ?? 0 | number }}</td>
                <td class="text-end pe-4">{{ item.debtsSaved ?? 0 | number }}</td>
            </ng-template>
        </app-table>

        <!-- Alert Empty Table -->
        <app-empty-alert-message [show]="!isLoading && items.length === 0" containerClass="m-2"/>

        <app-pagination
                [show]="items.length > 0"
                [itemsAmount]="pagination.itemsAmount"
                [itemsPerPage]="filters.size"
                [currentPage]="currentPage"
                (onChangeItemsPerPage)="changeItemsPerPage($event)"
                (onPaginate)="paginate($event)"/>
    </div>
</div>

<!-- Expanded Error Details (outside table) -->
@if (expandedRowId() !== null) {
    @for (item of items; track item.id) {
        @if (item.id === expandedRowId() && item.errorMessage) {
            <div class="card mt-3">
                <div class="card-body">
                    <div class="alert alert-danger mb-0">
                        <div class="d-flex justify-content-between align-items-start">
                            <strong><i class="ti ti-alert-triangle me-2"></i>Mensagem de Erro (Execução #{{ item.id }}):</strong>
                            <button class="btn btn-sm btn-link p-0" (click)="toggleExpandRow(item.id)">
                                <i class="ti ti-x"></i>
                            </button>
                        </div>
                        <pre class="mb-0 mt-2">{{ item.errorMessage }}</pre>
                    </div>
                </div>
            </div>
        }
    }
}
